import type { Query } from "@tanstack/react-query";

/**
 * React Query root keys that must NOT be persisted to localStorage. These are
 * either large payloads (GPS tracks, videos, ML-export image frames, event /
 * cycle detail) or volatile, frequently-polled data (alerts, KPIs). Persisting
 * them filled the ~5 MB localStorage quota and crashed writes app-wide; they all
 * refetch cheaply on demand. Matched against the first element of a query key
 * (see each module's `api/config.ts` `QUERY_KEYS`).
 */
export const NON_PERSISTED_QUERY_KEYS: ReadonlySet<string> = new Set([
  "gps",
  "videos",
  "events",
  "truck-events",
  "truck-events-overview",
  "kpi-stats",
  "alerts",
  "event-detail",
  "cycle-detail",
  "ml-review-classifications",
  "ml-review-detail",
  "ml-export-run-detail",
]);

/** Persist the cache for at most an hour (the 24h gcTime keeps it in memory; we
 * just don't want stale day-old data rehydrated from disk). */
export const PERSIST_MAX_AGE = 1000 * 60 * 60;

/** Bump to invalidate all previously-persisted caches after a shape change.
 *  v2 (2026-07-24): force a clean slate on deploy — stale persisted caches were
 *  keeping old data around after the alert-window / timezone changes. */
export const PERSIST_BUSTER = "v2";

/**
 * Whether a query is small/stable enough to persist across reloads. Keys not in
 * {@link NON_PERSISTED_QUERY_KEYS} (and any non-string root key) are persisted.
 */
export const shouldPersistQuery = (query: Query): boolean => {
  const root = query.queryKey?.[0];
  return typeof root !== "string" || !NON_PERSISTED_QUERY_KEYS.has(root);
};
